// sales-flow-v3
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

// The sales flow, with how many deals are waiting at each step. On the Sales Flow page the steps are tabs
// (pass `active` + `onSelect`); elsewhere they link to that step on the Sales Flow page.
const live = (c) => !["COMPLETED", "TERMINATED"].includes(c.status);
export const STEPS = [
  { key: "capture", label: "Lead Capture", color: "#3B82F6", hint: "New and contacted leads", from: "leads", test: (l) => ["NEW", "CONTACTED"].includes(l.stage) },
  { key: "qualify", label: "Qualify", color: "#2563EB", hint: "Qualified leads (incl. site visit), ready for a proposal", from: "leads", test: (l) => ["QUALIFIED", "SITE_VISIT", "TENDER"].includes(l.stage) },
  { key: "sent", label: "Proposal Sent", color: "#D97706", hint: "Proposals to send, and sent ones awaiting design review", from: "proposals", test: (p) => p.status === "DRAFT" || (p.status === "SENT" && (p.design_review || "PENDING") === "PENDING") },
  { key: "review", label: "Design Review", color: "#8B5CF6", hint: "Reviewed - changes asked or approved, PO awaited", from: "proposals", test: (p) => p.status === "SENT" && ["CHANGES", "APPROVED"].includes(p.design_review) },
  { key: "po", label: "PO Won", color: "#16A34A", hint: "Client POs booked as contracts", from: "contracts", test: live },
  { key: "convert", label: "Convert to Project", color: "#10B981", hint: "Contracts waiting to become a project", from: "contracts", test: (c) => live(c) && !c.project_id },
];
export const CLOSED = { key: "closed", label: "Won / lost leads", from: "leads", test: (l) => ["WON", "LOST"].includes(l.stage) };
export const stepOf = (key) => [...STEPS, CLOSED].find((s) => s.key === key) || STEPS[0];
export const SALES_FLOW_PATH = "/erp/delivery/sales-flow";

const PAGE_STEPS = { leads: ["capture", "qualify"], proposals: ["sent", "review"], contracts: ["po", "convert"] };

export function ClientFlowBar({ current, version, active, onSelect }) {
  const [n, setN] = useState(null);
  useEffect(() => {
    let off = false;
    Promise.all(["leads", "proposals", "contracts"].map((e) => api.get(`/pipeline/${e}`).then((r) => r.data).catch(() => [])))
      .then(([leads, proposals, contracts]) => {
        if (off) return;
        const data = { leads, proposals, contracts };
        setN(Object.fromEntries([...STEPS, CLOSED].map((s) => [s.key, data[s.from].filter(s.test).length])));
      });
    return () => { off = true; };
  }, [version]);
  return (
    <div className="rounded-xl border border-border bg-card p-3" data-testid="client-flow-bar">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Sales flow</span>
        {onSelect && (
          <button type="button" onClick={() => onSelect("closed")} data-testid="flow-step-closed"
            className={`text-[11px] hover:underline ${active === "closed" ? "font-semibold text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            Won / lost leads{n ? ` (${n.closed})` : ""}
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {STEPS.map((s, i) => {
          const on = onSelect ? active === s.key : (PAGE_STEPS[current] || []).includes(s.key);
          const c = n ? n[s.key] : null;
          const cls = `inline-flex min-h-[44px] min-w-[104px] items-center justify-center gap-2 rounded-lg px-4 py-2 text-center text-sm font-semibold leading-tight text-white shadow-sm transition ${on ? "ring-2 ring-offset-2 ring-offset-[hsl(var(--card))]" : "opacity-75 hover:opacity-100"}`;
          const style = { background: s.color, "--tw-ring-color": s.color };
          const inner = (<>{s.label}{c !== null && <span data-testid={`flow-count-${s.key}`} className="rounded-full bg-white/25 px-1.5 text-[11px] font-bold tabular-nums">{c}</span>}</>);
          return (
            <div key={s.key} className="flex items-center gap-2">
              {onSelect
                ? <button type="button" title={s.hint} aria-current={on ? "step" : undefined} data-testid={`flow-step-${s.key}`} onClick={() => onSelect(s.key)} className={cls} style={style}>{inner}</button>
                : <Link to={`${SALES_FLOW_PATH}?step=${s.key}`} title={s.hint} aria-current={on ? "step" : undefined} data-testid={`flow-step-${s.key}`} className={cls} style={style}>{inner}</Link>}
              {i < STEPS.length - 1 && <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
